/**
 * Dashboard "Approval channel" section (mig 0052).
 *
 * The one place a human can bind, verify or drop the Telegram channel that
 * approves an agent's large purchases. It lives in the browser on purpose: the
 * backend routes read the session cookie and nothing else, so an API token —
 * the credential a compromised agent holds — cannot reach them. If this UI did
 * not exist, the channel could not exist either, and every large transaction
 * would keep getting the old hard refusal.
 *
 * Two states that look similar and are not:
 *   - BOUND but unverified — we have never successfully delivered to the chat.
 *     `users.telegram_id` comes from the login widget and proves only that the
 *     person once clicked a Telegram button on our site, not that they ever
 *     pressed START with @roamzy_bot. Treated as dead until a ping lands, which
 *     is what "Check channel" does.
 *   - VERIFIED but not yet eligible — the seasoning window has not elapsed. A
 *     channel created alongside the request it approves is not an independent
 *     factor, so it waits. We show the exact timestamp rather than a spinner.
 *
 * i18n: every string through `t('dash.settings.confirmChannel.X')`, 6 locales.
 */
import { escapeHtml } from '../../utils';
import { fmtDateTime } from '../../utils/format';
import { t } from '../../i18n';
import type { TranslationKey } from '../../i18n/types';

const tt = (suffix: string): string => t(`dash.settings.confirmChannel.${suffix}` as TranslationKey);

interface ChannelState {
  configured: boolean;
  seasoning_hours: number;
  can_ever_approve: boolean;
  anon_origin: boolean;
  telegram_linked: boolean;
  channel: {
    kind: string;
    established_at: string;
    verified_at: string | null;
    eligible_at: string | null;
    eligible: boolean;
  } | null;
}

/** Static shell. Contents are filled in after the state fetch. */
export function confirmChannelSection(): string {
  return `
<section class="dash-settings__group" data-confirm-channel-section>
  <h3>${escapeHtml(tt('groupTitle'))}</h3>
  <p class="dash-sub">${escapeHtml(tt('intro'))}</p>
  <div data-confirm-channel-body style="margin-top:12px;"></div>
  <p class="dash-sub dash-error" data-confirm-channel-error hidden style="margin-top:8px;"></p>
</section>`;
}

const fmt = (iso: string | null): string => fmtDateTime(iso);

function render(body: HTMLElement, s: ChannelState): void {
  // Order matters: report the hard exclusions before offering a button that
  // could never lead anywhere.
  if (!s.configured) {
    body.innerHTML = `<p class="dash-sub">${escapeHtml(tt('notConfigured'))}</p>`;
    return;
  }
  if (!s.can_ever_approve) {
    body.innerHTML = `<p class="dash-sub">${escapeHtml(tt('anonOrigin'))}</p>`;
    return;
  }
  if (!s.channel) {
    body.innerHTML = s.telegram_linked
      ? `<p class="dash-sub">${escapeHtml(tt('none'))}</p>
         <button type="button" class="btn btn--accent btn--sm" data-confirm-channel-bind>${escapeHtml(tt('bindButton'))}</button>`
      : `<p class="dash-sub">${escapeHtml(tt('needTelegram'))}</p>`;
    return;
  }

  const status = !s.channel.verified_at
    ? `<strong>${escapeHtml(tt('stateUnverified'))}</strong> — ${escapeHtml(tt('stateUnverifiedHint'))}`
    : s.channel.eligible
      ? `<strong>${escapeHtml(tt('stateActive'))}</strong>`
      : `<strong>${escapeHtml(tt('statePending'))}</strong> — ${escapeHtml(tt('statePendingHint'))} ${escapeHtml(fmt(s.channel.eligible_at))}`;

  body.innerHTML = `
    <p class="dash-sub">${status}</p>
    <p class="dash-sub">${escapeHtml(tt('boundAt'))} ${escapeHtml(fmt(s.channel.established_at))}</p>
    <div style="display:flex; gap:8px; flex-wrap:wrap; margin-top:8px;">
      <button type="button" class="btn btn--ghost btn--sm" data-confirm-channel-verify>${escapeHtml(tt('verifyButton'))}</button>
      <button type="button" class="btn btn--ghost btn--sm" data-confirm-channel-remove>${escapeHtml(tt('removeButton'))}</button>
    </div>`;
}

async function load(section: HTMLElement): Promise<void> {
  const body = section.querySelector<HTMLElement>('[data-confirm-channel-body]');
  const err = section.querySelector<HTMLElement>('[data-confirm-channel-error]');
  if (!body) return;
  if (err) err.hidden = true;
  try {
    const resp = await fetch('/api/me/confirm-channel', { credentials: 'include' });
    if (!resp.ok) { body.innerHTML = ''; return; }
    render(body, await resp.json() as ChannelState);
  } catch {
    if (err) { err.hidden = false; err.textContent = tt('loadFailed'); }
  }
}

async function call(section: HTMLElement, method: 'POST' | 'DELETE', path: string): Promise<void> {
  const err = section.querySelector<HTMLElement>('[data-confirm-channel-error]');
  if (err) err.hidden = true;
  try {
    const resp = await fetch(path, { method, credentials: 'include' });
    if (!resp.ok) {
      const data = await resp.json().catch(() => ({})) as { detail?: string; error?: string };
      if (err) {
        err.hidden = false;
        // Backend copy is deliberately specific ("press START with @roamzy_bot")
        // and more useful than any generic string we could substitute.
        err.textContent = data.detail ?? data.error ?? tt('actionFailed');
      }
    }
  } catch {
    if (err) { err.hidden = false; err.textContent = tt('actionFailed'); }
  }
  await load(section);
}

export function wireConfirmChannelHandlers(root: ParentNode = document): void {
  const section = root.querySelector<HTMLElement>('[data-confirm-channel-section]');
  if (!section || section.dataset.wired === '1') return;
  section.dataset.wired = '1';

  section.addEventListener('click', (ev) => {
    const el = ev.target as HTMLElement | null;
    if (el?.closest('[data-confirm-channel-bind]')) {
      void call(section, 'POST', '/api/me/confirm-channel/telegram');
    } else if (el?.closest('[data-confirm-channel-verify]')) {
      void call(section, 'POST', '/api/me/confirm-channel/telegram/verify');
    } else if (el?.closest('[data-confirm-channel-remove]')) {
      if (window.confirm(tt('removeConfirm'))) {
        void call(section, 'DELETE', '/api/me/confirm-channel/telegram');
      }
    }
  });

  void load(section);
}
